import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, Eye, Loader2, Pencil, Send, Trash2 } from 'lucide-react';
import Markdown from 'react-markdown';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import { quoteService } from '@/lib/api/quote.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';
import type { ServiceRequest, ServiceRequestVendor, CustomFieldDefinition } from '@om/shared-types';
import type { EventForCelebration } from '@/types/celebration.types';
import type { Quote } from '@/types/quote.types';

const SR_STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  open: { label: 'Open', className: 'bg-blue-100 text-blue-700' },
  quoted: { label: 'Quoted', className: 'bg-purple-100 text-purple-700' },
  under_review: { label: 'Under Review', className: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Closed', className: 'bg-emerald-100 text-emerald-700' },
  reopened: { label: 'Reopened', className: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-600' },
};

const VENDOR_STATUS_BADGE: Record<string, { label: string; className: string }> = {
  sent: { label: 'Sent', className: 'bg-blue-50 text-blue-600' },
  viewed: { label: 'Viewed', className: 'bg-amber-50 text-amber-700' },
  quoted: { label: 'Quoted', className: 'bg-purple-50 text-purple-700' },
  awarded: { label: 'Awarded ✓', className: 'bg-green-50 text-green-700' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-600' },
};

export function ServiceRequestDetailPage() {
  const { t } = useTranslation(['services', 'common']);
  const { celebrationId, serviceId, requestId } = useParams<{
    celebrationId: string;
    serviceId: string;
    requestId: string;
  }>();
  const navigate = useNavigate();
  const cid = Number(celebrationId);
  const sid = Number(serviceId);
  const rid = Number(requestId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [vendors, setVendors] = useState<ServiceRequestVendor[]>([]);
  const [events, setEvents] = useState<EventForCelebration[]>([]);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [latestQuoteByVendorId, setLatestQuoteByVendorId] = useState<Record<number, number>>({});
  const [removingVendorId, setRemovingVendorId] = useState<number | null>(null);
  const [postingContent, setPostingContent] = useState('');
  const [postingBusy, setPostingBusy] = useState(false);
  const [postingSaving, setPostingSaving] = useState(false);
  const [postingPreview, setPostingPreview] = useState(false);
  const postingTextareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResizeTextarea = useCallback(() => {
    const el = postingTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Auto-resize posting textarea when content changes (load, generate, etc.)
  useEffect(() => {
    // Defer to next frame so the DOM has rendered with the new value
    requestAnimationFrame(autoResizeTextarea);
  }, [postingContent, autoResizeTextarea]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [req, vendorList, celebration, shortlistSummary, quotes] = await Promise.all([
        serviceRequestApiService.getRequest(cid, rid),
        serviceRequestApiService.listVendors(cid, rid).catch((): ServiceRequestVendor[] => []),
        celebrationService.getCelebrationWithEvents(cid).catch(() => null),
        serviceSuggestionApiService.getShortlistSummary(cid).catch(() => null),
        quoteService.listQuotes(cid, { request_id: rid }).catch((): Quote[] => []),
      ]);
      setRequest(req);
      setVendors(vendorList);
      setEvents(celebration?.events ?? []);
      setPostingContent(req.posting_content ?? '');

      const quoteMap: Record<number, number> = {};
      for (const quote of quotes) {
        const existingQuoteId = quoteMap[quote.vendor_id];
        if (!existingQuoteId || quote.id > existingQuoteId) {
          quoteMap[quote.vendor_id] = quote.id;
        }
      }
      setLatestQuoteByVendorId(quoteMap);

      // Resolve custom field definitions from service template
      if (shortlistSummary && req.service_id) {
        const allItems = (shortlistSummary.by_category ?? []).flatMap((cat: any) => cat.items);
        const matched = allItems.find((item: any) => item.service_definition_id === req.service_id);
        setCustomFieldDefs(matched?.custom_fields ?? []);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load service request');
    } finally {
      setLoading(false);
    }
  }, [cid, rid]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRemoveVendor = async (vendorId: number, srvId: number) => {
    setRemovingVendorId(srvId);
    setActionError(null);
    try {
      await serviceRequestApiService.removeVendor(cid, rid, vendorId);
      setVendors(v => v.filter(x => x.id !== srvId));
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to remove vendor');
    } finally {
      setRemovingVendorId(null);
    }
  };

  const handleVendorStatusTransition = async (vendorId: number, srvId: number, status: 'awarded' | 'rejected') => {
    setActionError(null);
    try {
      const updated = await serviceRequestApiService.transitionVendorStatus(cid, rid, vendorId, status);
      setVendors(v => v.map(x => x.id === srvId ? updated : x));
      // Refresh main request to get updated status
      const refreshed = await serviceRequestApiService.getRequest(cid, rid);
      setRequest(refreshed);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  const handleGeneratePosting = async () => {
    setActionError(null);
    setPostingBusy(true);
    try {
      const updated = await serviceRequestApiService.generatePosting(cid, rid);
      setRequest(updated);
      setPostingContent(updated.posting_content ?? '');
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to generate posting content');
    } finally {
      setPostingBusy(false);
    }
  };

  const handleSavePostingContent = async () => {
    if (!request) return;
    setPostingSaving(true);
    setActionError(null);
    try {
      const updated = await serviceRequestApiService.updatePostingContent(cid, rid, postingContent);
      setRequest(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to save posting content');
    } finally {
      setPostingSaving(false);
    }
  };

  const handleTransitionPostingStatus = async (status: 'draft' | 'in_review' | 'ready_to_post') => {
    setActionError(null);
    try {
      const updated = await serviceRequestApiService.transitionPostingStatus(cid, rid, status);
      setRequest(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to update posting status');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">Loading…</span>
      </div>
    );
  }

  if (!request) {
    return (
      <PageContainer width="wide">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'Service request not found'}
        </div>
      </PageContainer>
    );
  }

  const srBadge = SR_STATUS_BADGE[request.status] ?? { label: request.status, className: 'bg-gray-100 text-gray-600' };
  const canEdit = ['draft', 'open'].includes(request.status);
  const eventMap = new Map(events.map(e => [e.id, e.title]));

  return (
    <PageContainer width="wide" className="space-y-5">
      <AppBreadcrumbs />

      {(error || actionError) && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {actionError ?? error}
          <button type="button" onClick={() => { setError(null); setActionError(null); }} className="ml-auto text-xs underline">dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${srBadge.className}`}>
            {srBadge.label}
          </span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${request.priority === 'must_have' ? 'bg-rose-100 text-rose-700' : 'bg-blue-50 text-blue-600'}`}>
            {request.priority === 'must_have' ? 'Must Have' : 'Optional'}
          </span>
        </div>
        <h1 className="text-xl font-semibold text-gray-900">{request.request_name}</h1>
        {request.service_name && (
          <p className="mt-0.5 text-xs text-gray-500">Service: {request.service_name}</p>
        )}
      </div>

      {/* Request Details */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900">Request Details</h2>
          {canEdit && (
            <button
              type="button"
              onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}/edit`)}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('services:actions.editRequest')}
            </button>
          )}
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Budget</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {request.budget != null ? `₹${request.budget.toLocaleString()}` : 'Not set'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Assigned To</dt>
            <dd className="mt-0.5 text-sm text-gray-900">{request.assigned_to || 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Created</dt>
            <dd className="mt-0.5 text-sm text-gray-900">{new Date(request.created_at).toLocaleDateString()}</dd>
          </div>
          {request.description && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Description</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-sm text-gray-900">{request.description}</dd>
            </div>
          )}
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Applies To Events</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {(request.applies_to_event_ids ?? []).length > 0
                ? request.applies_to_event_ids!.map(id => eventMap.get(id) ?? `Event #${id}`).join(', ')
                : 'No events selected'}
            </dd>
          </div>
          {request.custom_field_values && Object.keys(request.custom_field_values).length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Custom Fields</dt>
              <dd className="mt-1 grid gap-2 sm:grid-cols-2">
                {Object.entries(request.custom_field_values).map(([key, value]) => {
                  const def = customFieldDefs.find((d) => d.key === key);
                  return (
                    <div key={key} className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-xs font-medium text-gray-500">{def?.label ?? key}</p>
                      <p className="text-sm text-gray-900">{value || '—'}</p>
                    </div>
                  );
                })}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Posting Workflow */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Vendor Posting</h2>
          {request.posting_status !== 'none' && (
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
              request.posting_status === 'ready_to_post' ? 'bg-green-100 text-green-700' :
              request.posting_status === 'in_review' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {request.posting_status.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {/* Stale banner */}
        {request.posting_stale && request.posting_status !== 'none' && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Request details changed since posting was generated. Consider regenerating.
          </div>
        )}

        {/* Generate / Regenerate */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">AI Posting Content</p>
          <button
            type="button"
            disabled={postingBusy}
            onClick={handleGeneratePosting}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {postingBusy ? <><Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Working…</> : (request.posting_content ? 'Regenerate AI Posting' : 'Generate AI Posting')}
          </button>
        </div>

        {/* Edit / Preview posting content */}
        {request.posting_content && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Review & Edit Posting</p>
              <div className="inline-flex rounded-md border border-gray-200 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setPostingPreview(false)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-l-md ${!postingPreview ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-500 hover:text-gray-700'}`}
                >
                  <Pencil className="h-3 w-3" />Edit
                </button>
                <button
                  type="button"
                  onClick={() => setPostingPreview(true)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-r-md border-l border-gray-200 ${postingPreview ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-500 hover:text-gray-700'}`}
                >
                  <Eye className="h-3 w-3" />Preview
                </button>
              </div>
            </div>

            {postingPreview ? (
              <div className="prose prose-sm max-w-none rounded-md border border-gray-300 bg-white px-4 py-3">
                <Markdown>{postingContent || '*No content yet*'}</Markdown>
              </div>
            ) : (
              <textarea
                ref={postingTextareaRef}
                value={postingContent}
                onChange={(e) => { setPostingContent(e.target.value); autoResizeTextarea(); }}
                onInput={autoResizeTextarea}
                rows={1}
                disabled={request.status === 'open'}
                className="w-full resize-none overflow-hidden rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="AI-generated posting content will appear here…"
              />
            )}
            {request.status === 'open' && (
              <p className="text-xs text-gray-400">Content is locked after posting is sent to vendors. Regenerate to update.</p>
            )}
            {request.status !== 'open' && !postingPreview && (
              <button
                type="button"
                disabled={postingSaving || postingContent === (request.posting_content ?? '')}
                onClick={handleSavePostingContent}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {postingSaving ? <><Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Saving…</> : 'Save Changes'}
              </button>
            )}
          </div>
        )}

        {/* Posting status transitions */}
        {request.posting_content && request.posting_status !== 'none' && (
          <div className="flex flex-wrap gap-2">
            {request.posting_status === 'draft' && (
              <button
                type="button"
                onClick={() => handleTransitionPostingStatus('in_review')}
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
              >
                Mark for Review
              </button>
            )}
            {request.posting_status === 'in_review' && (
              <>
                <button
                  type="button"
                  onClick={() => handleTransitionPostingStatus('ready_to_post')}
                  className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                >
                  Approve — Ready to Post
                </button>
                <button
                  type="button"
                  onClick={() => handleTransitionPostingStatus('draft')}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Back to Draft
                </button>
              </>
            )}
            {request.posting_status === 'ready_to_post' && (
              <button
                type="button"
                onClick={() => handleTransitionPostingStatus('draft')}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Revert to Draft
              </button>
            )}
          </div>
        )}
      </div>

      {/* Vendors */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">
            Vendors ({vendors.length})
          </h2>
          {request.posting_status === 'ready_to_post' && (
            <button
              type="button"
              onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}/post`)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
            >
              <Send className="h-3.5 w-3.5" />
              Post to Vendors
            </button>
          )}
        </div>

        {vendors.length === 0 ? (
          <p className="text-sm text-gray-500">
            {request.posting_status === 'ready_to_post'
              ? 'No vendors yet. Post this request to start receiving proposals.'
              : 'Complete the posting workflow above before posting to vendors.'}
          </p>
        ) : (
          <div className="space-y-3">
            {vendors.map(v => {
              const vBadge = VENDOR_STATUS_BADGE[v.status] ?? { label: v.status, className: 'bg-gray-100 text-gray-600' };
              const latestProposal = v.proposals.length > 0 ? v.proposals[v.proposals.length - 1] : null;
              const isRejected = v.status === 'rejected';
              const isAwarded = v.status === 'awarded';
              const isQuoted = v.status === 'quoted';
              const canFinalizeJourney = isQuoted || isAwarded || ['under_review', 'closed'].includes(request.status);
              const quoteId = latestQuoteByVendorId[v.vendor_id];
              const detailButtonLabel = latestProposal ? 'View Proposal' : 'View Request';

              return (
                <div
                  key={v.id}
                  className={`rounded-lg border px-4 py-3 ${isRejected ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {v.vendor_name ?? `Vendor #${v.vendor_id}`}
                        </p>
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${vBadge.className}`}>
                          {vBadge.label}
                        </span>
                      </div>
                      {latestProposal && (
                        <p className="mt-0.5 text-xs text-gray-500">
                          Proposal: ₹{latestProposal.amount.toLocaleString()} (Round {latestProposal.round})
                        </p>
                      )}
                      <p className="mt-0.5 text-xs text-gray-400">
                        Sent: {v.invited_at ? new Date(v.invited_at).toLocaleDateString() : '—'}
                        {v.viewed_at && ` · Viewed: ${new Date(v.viewed_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    {!isRejected && (
                      <div className="shrink-0 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}/vendors/${v.vendor_id}`)}
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          {detailButtonLabel}
                        </button>
                        {quoteId && (
                          <button
                            type="button"
                            onClick={() => navigate(`/celebrations/${cid}/quotes/${quoteId}`)}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            View Quote
                          </button>
                        )}
                        {isQuoted && latestProposal && (
                          <button
                            type="button"
                            onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}/vendors/${v.vendor_id}`)}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            Counter
                          </button>
                        )}
                        {canFinalizeJourney && (
                          <button
                            type="button"
                            onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}/vendors/${v.vendor_id}/finalize`)}
                            className="rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                          >
                            Finalize
                          </button>
                        )}
                        {isQuoted && (
                          <button
                            type="button"
                            onClick={() => handleVendorStatusTransition(v.vendor_id, v.id, 'awarded')}
                            className="rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
                          >
                            Award
                          </button>
                        )}
                        {isAwarded && (
                          <button
                            type="button"
                            onClick={() => navigate(`/celebrations/${cid}/bookings`)}
                            className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-white hover:bg-primary/90"
                          >
                            Create Booking
                          </button>
                        )}
                        {!isAwarded && (
                          <button
                            type="button"
                            onClick={() => handleVendorStatusTransition(v.vendor_id, v.id, 'rejected')}
                            className="text-xs font-medium text-red-600 hover:underline"
                          >
                            Reject
                          </button>
                        )}
                        {['sent', 'viewed'].includes(v.status) && removingVendorId !== v.id && (
                          <button
                            type="button"
                            onClick={() => handleRemoveVendor(v.vendor_id, v.id)}
                            className="text-gray-400 hover:text-red-500"
                            title="Remove vendor"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {removingVendorId === v.id && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate(`/celebrations/${cid}/services/${sid}`)}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to {request.service_name ?? 'Service Requests'}
      </button>
    </PageContainer>
  );
}
